import { useState } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import axios from 'axios';
import { Upload, UserPlus, CheckCircle2, Plus, X, Mail, Link2, Users, ArrowRightLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function BulkInvite() {
  const [tab, setTab] = useState('new'); // 'new' or 'existing'
  const [entries, setEntries] = useState([{ name: '', email: '', role: 'caregiver' }]);
  const [existingEntries, setExistingEntries] = useState([{ email: '', role: 'caregiver' }]);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const addEntry = () => setEntries(prev => [...prev, { name: '', email: '', role: 'caregiver' }]);
  const removeEntry = (i) => setEntries(prev => prev.filter((_, idx) => idx !== i));
  const updateEntry = (i, field, value) => setEntries(prev => prev.map((e, idx) => idx === i ? { ...e, [field]: value } : e));

  const addExisting = () => setExistingEntries(prev => [...prev, { email: '', role: 'caregiver' }]);
  const removeExisting = (i) => setExistingEntries(prev => prev.filter((_, idx) => idx !== i));
  const updateExisting = (i, field, value) => setExistingEntries(prev => prev.map((e, idx) => idx === i ? { ...e, [field]: value } : e));

  const handleSubmitNew = async (e) => {
    e.preventDefault();
    const valid = entries.filter(e => e.name.trim() && e.email.trim());
    if (valid.length === 0) { toast.error('Add at least one valid entry'); return; }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/admin/bulk-invite`, { invitations: valid }, { withCredentials: true });
      setResult({ ...data, type: 'new' });
      toast.success(`${data.created} invitation(s) sent via email!`);
    } catch { toast.error('Failed to send invitations'); }
    setLoading(false);
  };

  const handleSubmitExisting = async (e) => {
    e.preventDefault();
    const valid = existingEntries.filter(e => e.email.trim());
    if (valid.length === 0) { toast.error('Add at least one email'); return; }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/admin/network-invite`, { invitations: valid }, { withCredentials: true });
      setResult({ ...data, type: 'existing' });
      toast.success(`${data.sent} network invite(s) sent!`);
    } catch { toast.error('Failed to send network invites'); }
    setLoading(false);
  };

  const resetForm = () => {
    setResult(null);
    setEntries([{ name: '', email: '', role: 'caregiver' }]);
    setExistingEntries([{ email: '', role: 'caregiver' }]);
  };

  return (
    <DashboardLayout>
      <div className="space-y-6 max-w-3xl" data-testid="bulk-invite-page">
        <div>
          <h1 className="text-2xl sm:text-3xl font-heading font-bold text-foreground">Bulk Invite</h1>
          <p className="text-muted-foreground mt-1">Invite new users or add existing platform users to your network</p>
        </div>

        {result ? (
          <Card className="glass-card border-border">
            <CardContent className="p-8 text-center space-y-4">
              <CheckCircle2 className="w-16 h-16 mx-auto text-[#00A6A0]" />
              <h2 className="text-xl font-heading font-bold text-foreground">
                {result.type === 'new' ? 'Invitations Sent!' : 'Network Invites Sent!'}
              </h2>
              <p className="text-sm text-muted-foreground">
                {result.type === 'new'
                  ? 'Invitation links have been automatically emailed to all recipients.'
                  : 'Network join requests have been emailed to existing users.'}
              </p>
              <div className="flex justify-center gap-8 text-sm">
                <div><span className="text-2xl font-bold text-[#00A6A0]">{result.created || result.sent || 0}</span><p className="text-muted-foreground">Sent</p></div>
                <div><span className="text-2xl font-bold text-amber-500">{result.skipped || 0}</span><p className="text-muted-foreground">Skipped</p></div>
                {(result.errors?.length || 0) > 0 && <div><span className="text-2xl font-bold text-red-400">{result.errors.length}</span><p className="text-muted-foreground">Errors</p></div>}
              </div>
              {result.errors?.length > 0 && (
                <div className="text-left mt-4 p-3 rounded-lg bg-red-500/10 border border-red-500/20">
                  {result.errors.map((err, i) => <p key={i} className="text-xs text-red-400">{err}</p>)}
                </div>
              )}
              <Button className="bg-[#00A6A0] hover:bg-[#00A6A0]/90 text-white mt-4" onClick={resetForm} data-testid="send-more-btn">Send More</Button>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Tab Switcher */}
            <div className="flex gap-1 p-1 rounded-xl bg-muted/50 border border-border">
              <button
                onClick={() => setTab('new')}
                className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${
                  tab === 'new' ? 'bg-[#00A6A0] text-white' : 'text-muted-foreground hover:text-foreground'
                }`}
                data-testid="tab-new-users"
              >
                <UserPlus className="w-4 h-4" /> New Users
              </button>
              <button
                onClick={() => setTab('existing')}
                className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${
                  tab === 'existing' ? 'bg-[#00A6A0] text-white' : 'text-muted-foreground hover:text-foreground'
                }`}
                data-testid="tab-existing-users"
              >
                <ArrowRightLeft className="w-4 h-4" /> Existing Users (Join Network)
              </button>
            </div>

            {/* New Users Tab */}
            {tab === 'new' && (
              <form onSubmit={handleSubmitNew} className="space-y-4">
                <Card className="glass-card border-border">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <Mail className="w-4 h-4 text-[#00A6A0]" />
                      <p className="text-sm text-muted-foreground">
                        Invitation links are <strong className="text-foreground">automatically emailed</strong> to recipients. They can sign up using the link.
                      </p>
                    </div>
                  </CardContent>
                </Card>
                {entries.map((entry, i) => (
                  <Card key={i} className="glass-card border-border" data-testid={`invite-entry-${i}`}>
                    <CardContent className="p-4">
                      <div className="flex items-end gap-3">
                        <div className="flex-1 space-y-1">
                          <Label className="text-xs text-muted-foreground">Name</Label>
                          <Input value={entry.name} onChange={(e) => updateEntry(i, 'name', e.target.value)} placeholder="Full name" className="bg-muted border-border text-foreground" data-testid={`invite-name-${i}`} />
                        </div>
                        <div className="flex-1 space-y-1">
                          <Label className="text-xs text-muted-foreground">Email</Label>
                          <Input type="email" value={entry.email} onChange={(e) => updateEntry(i, 'email', e.target.value)} placeholder="email@example.com" className="bg-muted border-border text-foreground" data-testid={`invite-email-${i}`} />
                        </div>
                        <div className="w-36 space-y-1">
                          <Label className="text-xs text-muted-foreground">Role</Label>
                          <Select value={entry.role} onValueChange={(v) => updateEntry(i, 'role', v)}>
                            <SelectTrigger className="bg-muted border-border text-foreground" data-testid={`invite-role-${i}`}><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="caregiver">Caregiver</SelectItem>
                              <SelectItem value="client">Client</SelectItem>
                              <SelectItem value="family">Family</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        {entries.length > 1 && (
                          <Button type="button" variant="ghost" size="icon" onClick={() => removeEntry(i)} className="text-red-400 hover:bg-red-500/10 flex-shrink-0"><X className="w-4 h-4" /></Button>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
                <div className="flex gap-3">
                  <Button type="button" variant="outline" onClick={addEntry} className="border-border text-foreground gap-2" data-testid="add-another-btn"><Plus className="w-4 h-4" />Add Another</Button>
                  <Button type="submit" disabled={loading} className="bg-[#00A6A0] hover:bg-[#00A6A0]/90 text-white gap-2" data-testid="send-invitations-btn">
                    <Mail className="w-4 h-4" />{loading ? 'Sending...' : `Send ${entries.length} Invitation(s)`}
                  </Button>
                </div>
              </form>
            )}

            {/* Existing Users Tab */}
            {tab === 'existing' && (
              <form onSubmit={handleSubmitExisting} className="space-y-4">
                <Card className="glass-card border-border">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 mb-1">
                      <ArrowRightLeft className="w-4 h-4 text-[#00A6A0]" />
                      <p className="text-sm font-medium text-foreground">Join Another Network</p>
                    </div>
                    <p className="text-xs text-muted-foreground ml-6">
                      For users already registered on FendanaCura who want to join your agency network.
                      They will receive an email invitation to link their existing account — <strong className="text-foreground">no reinstall needed</strong>.
                    </p>
                  </CardContent>
                </Card>
                {existingEntries.map((entry, i) => (
                  <Card key={i} className="glass-card border-border" data-testid={`network-invite-entry-${i}`}>
                    <CardContent className="p-4">
                      <div className="flex items-end gap-3">
                        <div className="flex-1 space-y-1">
                          <Label className="text-xs text-muted-foreground">Email (existing user)</Label>
                          <Input type="email" value={entry.email} onChange={(e) => updateExisting(i, 'email', e.target.value)} placeholder="existing.user@example.com" className="bg-muted border-border text-foreground" data-testid={`network-email-${i}`} />
                        </div>
                        <div className="w-36 space-y-1">
                          <Label className="text-xs text-muted-foreground">Role in your network</Label>
                          <Select value={entry.role} onValueChange={(v) => updateExisting(i, 'role', v)}>
                            <SelectTrigger className="bg-muted border-border text-foreground" data-testid={`network-role-${i}`}><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="caregiver">Caregiver</SelectItem>
                              <SelectItem value="agency_admin">Admin</SelectItem>
                              <SelectItem value="client">Client</SelectItem>
                              <SelectItem value="family">Family</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        {existingEntries.length > 1 && (
                          <Button type="button" variant="ghost" size="icon" onClick={() => removeExisting(i)} className="text-red-400 hover:bg-red-500/10 flex-shrink-0"><X className="w-4 h-4" /></Button>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
                <div className="flex gap-3">
                  <Button type="button" variant="outline" onClick={addExisting} className="border-border text-foreground gap-2" data-testid="add-another-network-btn"><Plus className="w-4 h-4" />Add Another</Button>
                  <Button type="submit" disabled={loading} className="bg-[#00A6A0] hover:bg-[#00A6A0]/90 text-white gap-2" data-testid="send-network-invites-btn">
                    <Link2 className="w-4 h-4" />{loading ? 'Sending...' : `Send ${existingEntries.length} Network Invite(s)`}
                  </Button>
                </div>
              </form>
            )}
          </>
        )}
      </div>
    </DashboardLayout>
  );
}
